import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { CardTitle } from './card'
import { useCountUp } from '@/hooks/useCountUp'
import { placed } from '@/lib/style-guard'
import type { ContentProps } from '@/lib/content-props'
import { Inline } from './row'

/**
 * The Parley card vocabulary (24.10.2026): a warm card, a figure, a heading and a chip.
 * David, same day: «no text heavy dashboards». A card leads with one of these, not a paragraph.
 */

/** A Parley card: warm white, 22px corners, the soft long shadow. */
export function BentoCard({
  children,
  className,
  tone = 'default',
  as: Tag = 'article',
  roomy = false,
  ...rest
}: {
  children: ReactNode
  className?: string
  /** A card that holds one centred outcome (a closed tool): more room around it. */
  roomy?: boolean
  /** `hero` and `ink` are neutral since the semantic pass (their colour carried no meaning);
   *  `photo` is a card over a cover image: light text, and the hero tokens re-pointed so its
   *  chips, meter track and links stay visible on the scrim. */
  tone?: 'default' | 'hero' | 'ink' | 'photo'
  as?: 'article' | 'section' | 'div'
} & React.HTMLAttributes<HTMLElement>) {
  return (
    <Tag
      {...rest}
      className={cn(
        // `enter`: a card arriving fades in place once (Parley motion; tokens in index.css).
        'enter min-w-0 rounded-[var(--card-radius)]',
        roomy ? 'px-6 py-10' : 'p-5 sm:p-[22px]',
        tone !== 'photo' && 'border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--card-shadow)]',
        tone === 'photo' && 'border border-transparent bg-[var(--rail-bg)] text-[var(--photo-fg)] [--hero-card-fg:var(--photo-fg)] [--hero-card-chip:var(--scrim-medium)] [--hero-card-track:var(--photo-track)] [--data-pending:var(--photo-fg)]',
        placed(className, 'BentoCard', rest),
      )}
    >
      {children}
    </Tag>
  )
}

/** The figure: Bricolage at display weight, tabular. A plain number animates subsequent updates. */
/**
 * The figure scale, in one place (sweep 24.10.2026: thirteen call sites hand-set five px sizes).
 * `hero` is the answers number, `lg` a card's lead figure, `md` a stat card, `sm` a list's day
 * count, `of` the «/ 26» beside a figure.
 */
const FIGURE_SIZE = {
  hero: 'text-[52px] sm:text-[56px]',
  lg: 'text-[40px]',
  md: 'text-[34px]',
  sm: 'text-[28px]',
  of: 'text-[22px]',
} as const

export function Figure({ children, className, size = 'lg', muted }: { children: ReactNode; className?: string; size?: keyof typeof FIGURE_SIZE; /** A secondary figure: on a light card or on an ink card. */ muted?: 'light' | 'ink' }) {
  const ref = useCountUp<HTMLSpanElement>(typeof children === 'number' ? children : null)
  return (
    <span className={cn('inline-grid font-[family-name:var(--font-display)] font-semibold tabular-nums tracking-[-0.02em]', FIGURE_SIZE[size], 'leading-none', muted === 'light' && 'text-[var(--text-muted)]', muted === 'ink' && 'text-[var(--ink-card-fg-muted)]', placed(className, 'Figure'))}>
      <span aria-hidden="true" className="invisible col-start-1 row-start-1">{children}</span>
      <span ref={ref} className="col-start-1 row-start-1">{children}</span>
    </span>
  )
}

/** The card's heading: the shared CardTitle (level from context), set in the display face.
 *  Colour inherits, so it reads on the hero and ink cards too. */
export function CardHeading({ children, className, id, clamp }: { children: ReactNode; className?: string; id?: string } & Pick<ContentProps, 'clamp'>) {
  return (
    <CardTitle id={id} display tone="inherit" clamp={clamp} className={placed(className, 'CardHeading')}>
      {children}
    </CardTitle>
  )
}

/** Status pill used inside cards. Parley's status chips are fully rounded. */
/** The chip's fill follows the card it sits on: `hero` and `ink` cards, or a plain muted chip. */
const CHIP_TONE = {
  default: '',
  hero: 'bg-[var(--hero-card-chip)]',
  ink: 'bg-[var(--ink-card-raised)] text-[var(--ink-card-fg-muted)]',
  muted: 'bg-[var(--surface-muted)] text-[var(--text-secondary)]',
} as const

export function Chip({ children, className, tone = 'default' }: { children: ReactNode; className?: string; tone?: keyof typeof CHIP_TONE }) {
  return (
    // The capsule's look on its own element; Inline inside does the layout, and stays placement only.
    <span className={cn('content-fit inline-flex min-h-6 rounded-full px-2.5 text-small font-semibold', CHIP_TONE[tone], placed(className, 'Chip'))}> {/* signature-ok: retain the existing Parley status capsule, not an action shape. */}
      <Inline gap="xs">{children}</Inline>
    </span>
  )
}

